// src/App.js
import { ThemeProvider } from "@mui/material/styles";
import {
  Alert, Backdrop, Box, Button as MuiButton, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, Paper, Stack, Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import RefreshIcon from "@mui/icons-material/Refresh";

import DebugPanel from "./components/DebugPanel";
import T4RAppBar from "./components/T4RLJDAgentAppBar";
import AboutLJDAgent from "./components/AboutLJDAgent";
import Step0Onboarding from "./steps/Step0Onboarding";
import Step1OrgGoal from "./steps/Step1OrgGoal";
import Step2LearnerProfile from "./steps/Step2LearnerProfile";
import Step3FutureRoles from "./steps/Step3FutureRoles";
import Step4CurrentKnowledge from "./steps/Step4CurrentKnowledge";
import Step5Suggestions from "./steps/Step5Suggestions";
import Step6PreviewNotes from "./steps/Step6PreviewNotes";
import Step7AssembleJourney from "./steps/Step7AssembleJourney";
import LlmProcessingCard from "./components/LlmProcessingCard/LlmProcessingCard";
import JourneyStepper from "./components/JourneyStepper";
import StepNavigationBar from "./components/StepNavigationBar";
import CookieConsentDialog from "./components/session/CookieConsentDialog";
import StartChoiceDialog from "./components/session/StartChoiceDialog";
import SessionPanel from "./components/session/SessionPanel";
import StepToolbar from "./components/session/StepToolbar";
import SampleCityBand from "./components/session/SampleCityBand";
import AssistantPanel from "./components/assistant/AssistantPanel";
import ChatbotPrepareDialog from "./components/assistant/ChatbotPrepareDialog";
import { AssistantBanner, AssistantFieldsContext } from "./components/common/AssistantFields";
import { StaleBanner } from "./components/common/StepStatusBanner";
import { MluDetailsProvider } from "./components/common/MluDetails";
import RunsLeft from "./components/common/RunsLeft";
import BotCheckDialog from "./components/common/BotCheckDialog";

import {
  STEPS, ROLE_OPTIONS, LLM_PROCESSING_CONCEPTS_STEP_5, LLM_PROCESSING_CONCEPTS_STEP_7, LLM_DISCLAIMER_TEXT,
  STEP5_LLM_FLOW_BLOCKS, STEP5_LLM_FLOW_DETAILS_CONFIG, STEP7_LLM_FLOW_BLOCKS, STEP7_LLM_FLOW_DETAILS_CONFIG,
} from "./config/t4rConfig";
import { PRODUCT_NAME } from "./config/product";
import t4rTheme from "./theme/t4rTheme";
import { useLearningJourney } from "./hooks/useLearningJourney";
import { useSessionBootstrap } from "./hooks/useSessionBootstrap";
import LoginPage from "./LoginPage";

/** The 7-step flow, shown once a session exists. */
function LJDAppShell({ initialStep, auth, onLogout }) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [chatbotStep, setChatbotStep] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const headingRef = useRef(null);
  const initialApplied = useRef(false);

  // Alt + Shift + D toggles the debug panel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.shiftKey && !event.ctrlKey && !event.metaKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setShowDebugPanel((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Text size accessibility scaling
  const [textSizeScale, setTextSizeScale] = useState(() => {
    try {
      const saved = window.localStorage.getItem("t4r_text_scale");
      return saved ? parseFloat(saved) : 1.0;
    } catch {
      return 1.0;
    }
  });
  useEffect(() => {
    document.documentElement.style.fontSize = `${textSizeScale * 100}%`;
    try {
      window.localStorage.setItem("t4r_text_scale", textSizeScale.toString());
    } catch {
      /* ignore */
    }
  }, [textSizeScale]);

  const j = useLearningJourney({ sessionReady: true });
  const { activeStep, setActiveStep, setSnack, snack, summary, stepViews } = j;

  // Resume at the step the user stopped (or the step a review link points to).
  useEffect(() => {
    if (j.hydrated && !initialApplied.current) {
      initialApplied.current = true;
      if (initialStep > 0) {
        j.setStep0Completed(true);
        setActiveStep(Math.min(initialStep, 7));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [j.hydrated]);

  // On a step change: start at the top of the new step and move keyboard / screen-reader focus there.
  useEffect(() => {
    if (!initialApplied.current) return;
    window.scrollTo({ top: 0 });
    headingRef.current?.focus({ preventScroll: true });
  }, [activeStep]);

  const handleStep0Complete = useCallback((isValid) => {
    j.setStep0Completed(isValid);
    j.updateStepValidity(0, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apiBase = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");
  const view = stepViews[activeStep];
  const assistantFields = j.assistantFields(activeStep);

  const onImported = (step, v) => {
    j.applyStep(step, v.data);
    j.setView(step, v);
    j.hydrate().catch(() => {});
    setSnack({ type: "success", text: `The chatbot's answer was filled in for step ${step}. Please review it and confirm.` });
  };

  const aiFooter = (step) => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mt: 2 }}>
      <MuiButton
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        disabled={step === 5 ? j.step5Loading : j.step7Loading}
        onClick={() => {
          if (step === 7 && stepViews[7]?.source === "user" &&
            !window.confirm("Generating a new plan replaces your edits. Continue?")) return;
          j.runAi(step, { force: true });
        }}
      >
        {step === 5 ? "Generate new suggestions" : "Generate a new plan"}
      </MuiButton>
      <RunsLeft summary={summary} step={step} />
    </Stack>
  );

  const appBarActions = [
    { key: "assistant", label: "AI assistant", icon: <SmartToyOutlinedIcon />, onClick: () => setAssistantOpen(true) },
    { key: "session", label: "Your session", icon: <BookmarkBorderIcon />, onClick: () => setSessionOpen(true) },
  ];

  return (
    <MluDetailsProvider>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: "column", gap: 2 }} open={j.isAddingToCart || false}>
        <CircularProgress color="inherit" />
        <Typography variant="h6">Creating Learning Journey...</Typography>
        <Typography variant="body2">
          {j.cartTotalCount > 0 ? `Processing... ${j.cartAddCount} / ${j.cartTotalCount} MLUs identified` : "Redirecting you to the platform in just a moment."}
        </Typography>
      </Backdrop>

      <Dialog open={j.showDownloadWarning} onClose={() => j.setShowDownloadWarning(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1.5 }}>
          <WarningAmberIcon color="warning" /> Download your Learning Journey
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            It is important that you <strong>download</strong> the Learning Journey (the session table and alignment logs) before moving on to the K8-T4R-Website.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => j.setShowDownloadWarning(false)} color="inherit">Go back</MuiButton>
          <MuiButton variant="contained" onClick={() => {
            j.setHasDownloadedJourney(true);
            j.setShowDownloadWarning(false);
            setTimeout(j.handleStep7Next, 100);
          }}>
            I understand, proceed anyway
          </MuiButton>
        </DialogActions>
      </Dialog>

      <T4RAppBar
        isLlmProcessing={j.step5Loading || j.step7Loading}
        onOpenAbout={() => setAboutOpen(true)}
        extraActions={appBarActions}
        user={auth?.email}
        onLogout={onLogout}
        textSizeScale={textSizeScale}
        onTextSizeChange={setTextSizeScale}
      />
      <AboutLJDAgent open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <AssistantPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        onCodesChanged={() => j.refreshSummary().catch(() => {})}
        onOpenChatbot={() => { setAssistantOpen(false); setChatbotStep(activeStep >= 1 && activeStep <= 4 ? activeStep : 1); }}
      />
      <ChatbotPrepareDialog open={!!chatbotStep} step={chatbotStep} onClose={() => setChatbotStep(null)} onImported={onImported} />
      <SessionPanel open={sessionOpen} onClose={() => setSessionOpen(false)} summary={summary} />
      <BotCheckDialog
        open={!!j.botCheckPending}
        onClose={() => j.setBotCheckPending(null)}
        onVerified={async () => {
          const pending = j.botCheckPending;
          j.setBotCheckPending(null);
          const s = await j.refreshSummary();
          if (pending && !s.bot_check_required) j.runAi(pending.step, { force: pending.force });
        }}
      />

      <Container component="main" maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          {showDebugPanel && (
            <DebugPanel
              apiBase={apiBase}
              sessionId={summary ? `cookie session (created ${summary.session.created_at})` : null}
              health={null}
              onFillDummyValues={j.fillDummyValues}
              onFillDummyValuesCertification={j.fillDummyValuesCertification}
              futureSelection={j.futureSelection}
              shortlistPreview={j.shortlistPreview}
              showRawStep5={j.showRawStep5}
              setShowRawStep5={j.setShowRawStep5}
              marginBottom={5}
            />
          )}

          <Stack spacing={2}>
            <nav aria-label="Steps" ref={headingRef} tabIndex={-1} style={{ outline: "none" }}>
              <JourneyStepper steps={STEPS} activeStep={activeStep} />
            </nav>

            {/* One-time offer right after onboarding; quiet and remembered once used or dismissed */}
            {activeStep === 1 && !j.sampleActive && !j.orgGoal?.trim() && <SampleCityBand onTry={j.fillSampleCity} />}

            {activeStep >= 1 && (
              <StepToolbar
                step={activeStep}
                status={view?.status}
                onChatbot={() => setChatbotStep(activeStep)}
                onAssistant={() => setAssistantOpen(true)}
              />
            )}

            {j.sampleActive && activeStep >= 1 && activeStep <= 4 && (
              <Alert severity="info" icon={<LocationCityIcon />}>
                <strong>Sample data.</strong> These answers are an example. Replace them with your own and confirm each step.
              </Alert>
            )}
            <AssistantBanner fields={assistantFields} status={view?.status} />
            <AheadBanner activeStep={activeStep} stepViews={stepViews} />
            <StaleBanner status={view?.status} step={activeStep} />

            <AssistantFieldsContext.Provider value={assistantFields}>
              {activeStep === 0 && (
                <Step0Onboarding onComplete={handleStep0Complete} onOpenAssistant={() => setAssistantOpen(true)} />
              )}
              {activeStep === 1 && (
                <Step1OrgGoal
                  orgGoal={j.orgGoal}
                  onChangeOrgGoal={j.setOrgGoal}
                  certificationNeeded={j.certificationNeeded}
                  onChangeCertificationNeeded={j.setCertificationNeeded}
                  onValidityChange={(isValid) => j.updateStepValidity(1, isValid)}
                />
              )}
              {activeStep === 2 && (
                <Step2LearnerProfile profile={j.profile} setProfile={j.setProfile} onValidityChange={(isValid) => j.updateStepValidity(2, isValid)} />
              )}
              {activeStep === 3 && (
                <Step3FutureRoles
                  roleOptions={ROLE_OPTIONS}
                  futureRoles={j.futureRoles}
                  setFutureRoles={j.setFutureRoles}
                  customRoleInput={j.customRoleInput}
                  setCustomRoleInput={j.setCustomRoleInput}
                  expectedContribution={j.expectedContribution}
                  setExpectedContribution={j.setExpectedContribution}
                />
              )}
              {activeStep === 4 && (
                <Step4CurrentKnowledge
                  selEdu={j.selEdu} setSelEdu={j.setSelEdu} selGov={j.selGov} setSelGov={j.setSelGov}
                  selTech={j.selTech} setSelTech={j.setSelTech} selEth={j.selEth} setSelEth={j.setSelEth}
                  certificationNeeded={j.certificationNeeded}
                  assistantSummary={stepViews[4]?.data?.prior_knowledge || ""}
                  matchedIds={stepViews[4]?.data?.matched_ids || []}
                  matchWhy={stepViews[4]?.data?.match_why || {}}
                />
              )}
            </AssistantFieldsContext.Provider>

            {activeStep === 5 && !j.hasAcknowledgedStep5 && (
              <>
                <LlmProcessingCard
                  loading={j.step5Loading}
                  onProceed={j.handleAcknowledgeStep5}
                  concepts={LLM_PROCESSING_CONCEPTS_STEP_5}
                  disclaimerText={LLM_DISCLAIMER_TEXT}
                  flowBlocks={STEP5_LLM_FLOW_BLOCKS}
                  flowDetailsConfig={STEP5_LLM_FLOW_DETAILS_CONFIG}
                />
                <RunsLeft summary={summary} step={5} />
              </>
            )}
            {activeStep === 5 && j.hasAcknowledgedStep5 && (
              <Box>
                {j.step5Meta.fallback && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The AI was unavailable, so these are simple rule-based suggestions in catalogue order. Try again later for tailored suggestions.
                  </Alert>
                )}
                <Step5Suggestions
                  futureSelection={j.futureSelection}
                  shortlistPreview={j.shortlistPreview}
                  loading={j.step5Loading}
                  error={j.step5Error?.message || null}
                  onRetry={j.step5Error?.code === "session_limit" || j.step5Error?.code === "daily_cap" ? null : j.handleRetryStep5}
                  certificationNeeded={j.certificationNeeded}
                />
                {(j.futureSelection || j.step5Error) && aiFooter(5)}
              </Box>
            )}

            {activeStep === 6 && (
              <Step6PreviewNotes
                preview={j.preview}
                onRefreshPreview={j.handleStep6Preview}
                onClearPreview={j.handleClearPreview}
                futureEdu={j.futureEdu} setFutureEdu={j.setFutureEdu}
                futureGov={j.futureGov} setFutureGov={j.setFutureGov}
                futureTech={j.futureTech} setFutureTech={j.setFutureTech}
                futureEth={j.futureEth} setFutureEth={j.setFutureEth}
                certificationNeeded={j.certificationNeeded}
                alignmentNotes={j.alignmentNotes}
                setAlignmentNotes={j.setAlignmentNotes}
                notesByFramework={j.notesByFramework}
                setNotesByFramework={j.setNotesByFramework}
                shortlistPreview={j.shortlistPreview}
                onSelectionValidityChange={j.setStep6HasSelection}
              />
            )}

            {activeStep === 7 && !j.hasAcknowledgedStep7 && (
              <>
                <LlmProcessingCard
                  loading={j.step7Loading}
                  onProceed={j.handleAcknowledgeStep7}
                  concepts={LLM_PROCESSING_CONCEPTS_STEP_7}
                  disclaimerText={LLM_DISCLAIMER_TEXT}
                  flowBlocks={STEP7_LLM_FLOW_BLOCKS}
                  flowDetailsConfig={STEP7_LLM_FLOW_DETAILS_CONFIG}
                />
                <RunsLeft summary={summary} step={7} />
              </>
            )}
            {activeStep === 7 && j.hasAcknowledgedStep7 && (
              <Step7AssembleJourney
                finalMd={j.finalMd}
                loading={j.step7Loading}
                error={j.step7Error?.message || null}
                onRetry={j.step7Error?.code === "session_limit" || j.step7Error?.code === "daily_cap" ? null : j.handleRetryStep7}
                journeySummary={j.journeySummary}
                onDownload={j.markJourneyAsDownloaded}
                plan={j.plan}
                onPlanChange={j.setPlan}
                aiFooter={aiFooter(7)}
              />
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end", minHeight: 48 }} aria-live="polite">
              {snack && (
                <Alert severity={snack.type} onClose={() => setSnack(null)} sx={{ width: "fit-content" }}>{snack.text}</Alert>
              )}
            </Box>

            {[1, 2, 3, 4, 6].includes(activeStep) && view?.status !== "confirmed" && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                Your answers are saved automatically as a draft. Press <strong>Confirm</strong> when you are happy with this step.
              </Typography>
            )}
            {activeStep === 7 && j.hasAcknowledgedStep7 && (j.plan?.rows || []).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                Your edits to the teaching plan are saved automatically. Download the journey when you are done.
              </Typography>
            )}

            <StepNavigationBar
              activeStep={activeStep}
              canGoBack={activeStep !== 0}
              onBack={() => { setSnack(null); setActiveStep((s) => s - 1); }}
              onNext={async () => {
                setSnack(null);
                if (activeStep === 7) await j.handleStep7Next();
                else setActiveStep((s) => Math.min(7, s + 1));
              }}
              saveHandler={j.saveHandler}
              saveLabel={j.saveLabel}
              isSaveDisabled={j.isSaveDisabled}
              isNextDisabled={j.isNextDisabled}
              nextLabel={activeStep === 7 ? (j.isAddingToCart ? "Adding to Cart..." : "Connect to website to download full MLUs") : "Next"}
            />
          </Stack>
        </Paper>
      </Container>
    </MluDetailsProvider>
  );
}

/** The assistant fills steps in order, so it may be ahead of the user: say so on the earlier steps. */
function AheadBanner({ activeStep, stepViews }) {
  if (activeStep < 1 || activeStep > 3) return null;
  const ahead = [2, 3, 4].filter((n) => n > activeStep && stepViews[n]?.status === "proposed"
    && ["assistant", "llm"].includes(stepViews[n]?.source));
  if (!ahead.length) return null;
  const list = ahead.length === 1 ? `step ${ahead[0]}`
    : `steps ${ahead.slice(0, -1).join(", ")} and ${ahead[ahead.length - 1]}`;
  return (
    <Alert severity="info" icon={<SmartToyOutlinedIcon />} role="status">
      Your AI assistant has also prepared {list}. Review and confirm this step to continue to {ahead.length === 1 ? "it" : "them"}.
    </Alert>
  );
}

/** Cookie consent, resume link handling and "continue or start new" before the flow. */
function SessionGate({ auth, onLogout }) {
  const boot = useSessionBootstrap();
  if (boot.phase === "ready") {
    return <LJDAppShell initialStep={boot.initialStep} auth={auth} onLogout={onLogout} />;
  }
  return (
    <>
      <T4RAppBar onOpenAbout={() => {}} user={auth?.email} onLogout={onLogout} />
      <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
        {boot.phase === "loading" && <CircularProgress aria-label="Loading" />}
        {boot.phase === "error" && (
          <Alert severity="error" action={<MuiButton color="inherit" onClick={() => window.location.reload()}>Retry</MuiButton>}>
            {boot.error}
          </Alert>
        )}
        {(boot.phase === "consent" || boot.phase === "declined") && (
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>{PRODUCT_NAME}</Typography>
        )}
      </Container>
      <CookieConsentDialog
        open={boot.phase === "consent" || boot.phase === "declined"}
        declined={boot.phase === "declined"}
        onAccept={boot.accept}
        onDecline={boot.decline}
        onReview={boot.reviewConsent}
      />
      <StartChoiceDialog
        open={boot.phase === "choose"}
        journeys={boot.journeys}
        error={boot.error}
        onContinue={boot.continueSession}
        onStartNew={boot.startNew}
      />
    </>
  );
}

export default function App() {
  const isLoginRequired = process.env.REACT_APP_LOGIN_NEEDED === "true";
  const [auth, setAuth] = useState(() => {
    try {
      const raw = window.localStorage.getItem("t4r_auth");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [authExpired, setAuthExpired] = useState(false);

  const handleLogin = useCallback((payload) => {
    setAuth(payload);
    setAuthExpired(false);
    try {
      window.localStorage.setItem("t4r_auth", JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, []);

  const handleLogout = useCallback(() => {
    setAuth(null);
    setAuthExpired(false);
    try {
      window.localStorage.removeItem("t4r_auth");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onAuthExpired = () => setAuthExpired(true);
    window.addEventListener("t4r-auth-expired", onAuthExpired);
    return () => window.removeEventListener("t4r-auth-expired", onAuthExpired);
  }, []);

  return (
    <ThemeProvider theme={t4rTheme}>
      <Dialog open={isLoginRequired && authExpired && !!auth} disableEscapeKeyDown PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Session Expired</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your login session has expired or is no longer valid. Please log out and sign in again to continue using the {PRODUCT_NAME}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton variant="contained" color="primary" onClick={handleLogout}>Log Out &amp; Sign In Again</MuiButton>
        </DialogActions>
      </Dialog>

      {isLoginRequired && !auth ? (
        <LoginPage onLogin={handleLogin} />
      ) : (
        <SessionGate auth={auth} onLogout={isLoginRequired ? handleLogout : null} />
      )}
    </ThemeProvider>
  );
}
